/**
 * Email utility functions for generating mailto: links with pre-filled templates
 */

export interface EmailTemplate {
  subject: string
  body: string
}

/**
 * Generate mailto: URL
 */
export function createMailtoLink(email: string, template: EmailTemplate): string {
  const subject = encodeURIComponent(template.subject)
  const body = encodeURIComponent(template.body)
  return `mailto:${email}?subject=${subject}&body=${body}`
}

/**
 * Open email client with pre-filled email
 */
export function openEmailClient(email: string, template: EmailTemplate): void {
  const mailtoLink = createMailtoLink(email, template)
  window.location.href = mailtoLink
}

/**
 * Generate Teacher Login Credentials email template
 */
export function getTeacherLoginEmailTemplate(params: {
  teacherFirstName: string
  teacherEmail: string
  teacherPassword?: string
  parishName: string
  adminTitle?: string
  adminLastName: string
  loginUrl: string
  currentYear: number
}): EmailTemplate {
  const {
    teacherFirstName,
    teacherEmail,
    teacherPassword,
    parishName,
    adminTitle,
    adminLastName,
    loginUrl,
    currentYear,
  } = params

  const passwordLine = teacherPassword
    ? `Password: ${teacherPassword}`
    : `Password: [Your password will be set by the Administrator]`

  const subject = `${parishName} - Teacher Login Credentials`
  
  const body = `Hello ${teacherFirstName},

Thank you for teaching! See below for your login for the ${currentYear} teaching year:

Website login: ${loginUrl}
Username: ${teacherEmail}
${passwordLine}

Note that the Administrator User sets your password, you cannot reset it from the login page. Also note that your password will reset next year.

Please note that, if need be, you can register parents anytime via the QR Code or URL displayed on your Dashboard.

We look forward to another year of Catechism, and appreciate all your hard work in advance!

God bless,
${adminTitle ? `${adminTitle} ` : ''}${adminLastName}
${parishName}`

  return { subject, body }
}

/**
 * Generate Admin Login Credentials email template
 */
export function getAdminLoginEmailTemplate(params: {
  adminFirstName: string
  adminEmail: string
  adminPassword?: string
  parishName: string
  senderTitle?: string
  senderLastName: string
  loginUrl: string
}): EmailTemplate {
  const {
    adminFirstName,
    adminEmail,
    adminPassword,
    parishName,
    senderTitle,
    senderLastName,
    loginUrl,
  } = params

  const passwordLine = adminPassword
    ? `Password: ${adminPassword}`
    : `Password: [Your password will be set by the Administrator]`

  const subject = `${parishName} - Admin Login Credentials`
  
  const body = `Hello ${adminFirstName},

Your administrator account has been created for ${parishName}. See below for your login credentials:

Website login: ${loginUrl}
Username: ${adminEmail}
${passwordLine}

You can reset your password from the Settings page after logging in.

God bless,
${senderTitle ? `${senderTitle} ` : ''}${senderLastName}
${parishName}`

  return { subject, body }
}

/**
 * Open email client with multiple recipients (BCC for parents, CC for teachers)
 */
export function openEmailClientWithRecipients(
  parentEmails: string[],
  teacherEmails: string[],
  template: EmailTemplate
): void {
  const bcc = parentEmails.join(',')
  const cc = teacherEmails.join(',')
  const subject = encodeURIComponent(template.subject)
  const body = encodeURIComponent(template.body)
  
  let mailtoLink = `mailto:?bcc=${encodeURIComponent(bcc)}&cc=${encodeURIComponent(cc)}&subject=${subject}&body=${body}`
  window.location.href = mailtoLink
}

/**
 * Generate Generic Announcement email template
 */
export function getGenericAnnouncementEmailTemplate(params: {
  senderTitle?: string
  senderFirstName: string
  senderLastName: string
}): EmailTemplate {
  const { senderTitle, senderFirstName, senderLastName } = params
  
  const subject = 'Quick announcement'
  const body = `Dear Parents;

Regards,
${senderTitle ? `${senderTitle} ` : ''}${senderFirstName} ${senderLastName}`
  
  return { subject, body }
}

/**
 * Generate Class-Specific Announcement email template
 */
export function getClassSpecificAnnouncementEmailTemplate(params: {
  classNames: string[]
  senderTitle?: string
  senderFirstName: string
  senderLastName: string
}): EmailTemplate {
  const { classNames, senderTitle, senderFirstName, senderLastName } = params
  
  const subject = 'Quick announcement'
  const classesText = classNames.length > 0 ? ` for ${classNames.join(', ')}` : ''
  const body = `Dear Parents${classesText};

Regards,
${senderTitle ? `${senderTitle} ` : ''}${senderFirstName} ${senderLastName}`
  
  return { subject, body }
}

